import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import type { ObservedProgression } from "@/lib/progression";

interface Props {
  progression: ObservedProgression | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

/**
 * 9:16 cinematic archival card for an observed progression sequence.
 * Matte black, restrained typography, subtle cyan separators.
 */
const ProgressionArtifact = ({ progression, open, onOpenChange }: Props) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  if (!progression) return null;
  const { steps } = progression;

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement("a");
      link.download = `greyrock-progression-${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } finally {
      setExporting(false);
    }
  };

  // Adaptive type scale so longer chains stay legible
  const stageFontSize = steps.length >= 5 ? 18 : steps.length === 4 ? 20 : 22;
  const gap = steps.length >= 5 ? 18 : 24;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 bg-transparent border-none shadow-none">
        <button
          onClick={() => onOpenChange(false)}
          className="absolute -top-10 right-0 text-muted-foreground/60 hover:text-foreground z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex justify-center">
          <div
            ref={cardRef}
            className="relative overflow-hidden"
            style={{
              width: 360,
              height: 640,
              background:
                "radial-gradient(120% 80% at 50% 0%, hsl(184 100% 50% / 0.05), transparent 60%), linear-gradient(180deg, hsl(0 0% 5%) 0%, hsl(0 0% 2%) 100%)",
            }}
          >
            {/* archival grid */}
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.035]"
              style={{
                backgroundImage:
                  "linear-gradient(hsl(0 0% 100% / 1) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100% / 1) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />
            {/* top hairline */}
            <div
              aria-hidden
              className="absolute top-0 left-0 right-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent, hsl(184 100% 50% / 0.35), transparent)",
              }}
            />
            {/* corner marks */}
            <div aria-hidden className="absolute top-3 left-3 w-3 h-3 border-l border-t" style={{ borderColor: "hsl(184 100% 50% / 0.3)" }} />
            <div aria-hidden className="absolute top-3 right-3 w-3 h-3 border-r border-t" style={{ borderColor: "hsl(184 100% 50% / 0.3)" }} />
            <div aria-hidden className="absolute bottom-3 left-3 w-3 h-3 border-l border-b" style={{ borderColor: "hsl(184 100% 50% / 0.3)" }} />
            <div aria-hidden className="absolute bottom-3 right-3 w-3 h-3 border-r border-b" style={{ borderColor: "hsl(184 100% 50% / 0.3)" }} />

            <div className="relative h-full flex flex-col justify-between px-8 py-10">
              {/* Header */}
              <div className="text-center space-y-1.5">
                <p
                  className="text-[9px] uppercase font-mono"
                  style={{ letterSpacing: "0.36em", color: "hsl(184 100% 50% / 0.7)" }}
                >
                  Observed Progression
                </p>
                <p
                  className="text-[8px] uppercase font-mono"
                  style={{ letterSpacing: "0.28em", color: "hsl(0 0% 100% / 0.32)" }}
                >
                  Conversational Sequence
                </p>
              </div>

              {/* Sequence */}
              <ol
                className="flex flex-col items-center justify-center flex-1"
                style={{ gap: 0 }}
              >
                {steps.map((step, i) => (
                  <li key={`${step.stage}-${i}`} className="flex flex-col items-center">
                    <p
                      className="font-light text-center"
                      style={{
                        fontSize: stageFontSize,
                        letterSpacing: "0.24em",
                        color: "hsl(0 0% 100% / 0.92)",
                        textTransform: "uppercase",
                      }}
                    >
                      {step.stage}
                    </p>
                    {i < steps.length - 1 && (
                      <div
                        className="flex flex-col items-center"
                        style={{ marginTop: gap, marginBottom: gap }}
                      >
                        <div
                          style={{
                            width: 1,
                            height: 18,
                            background:
                              "linear-gradient(180deg, hsl(184 100% 50% / 0.4), hsl(184 100% 50% / 0.05))",
                          }}
                        />
                        <span
                          style={{
                            fontSize: 11,
                            color: "hsl(184 100% 50% / 0.5)",
                            lineHeight: 1,
                            marginTop: 4,
                          }}
                        >
                          ↓
                        </span>
                      </div>
                    )}
                  </li>
                ))}
              </ol>

              {/* Footer */}
              <div className="space-y-2">
                <div
                  aria-hidden
                  className="h-px w-12 mx-auto"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, hsl(184 100% 50% / 0.5), transparent)",
                  }}
                />
                <p
                  className="text-[8px] uppercase font-mono text-center"
                  style={{ letterSpacing: "0.32em", color: "hsl(0 0% 100% / 0.42)" }}
                >
                  Observed during rehearsal archive
                </p>
                <div className="flex items-center justify-between pt-1">
                  <p
                    className="text-[10px] font-semibold"
                    style={{ color: "hsl(0 0% 100% / 0.65)", letterSpacing: "0.04em" }}
                  >
                    GreyRock AI®
                  </p>
                  <p
                    className="text-[8px] font-mono"
                    style={{ color: "hsl(0 0% 100% / 0.25)", letterSpacing: "0.18em" }}
                  >
                    greyrockai.app
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-center">
          <Button
            onClick={handleDownload}
            disabled={exporting}
            variant="outline"
            size="sm"
            className="text-xs tracking-wider uppercase font-mono border-cyan/30 text-cyan/90 hover:bg-cyan/10"
          >
            <Download className="w-3.5 h-3.5 mr-2" />
            {exporting ? "Preserving…" : "Preserve Progression"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProgressionArtifact;
